/*
 * Project: BookingEngine
 * Module: components/booking/rooms/PictureSlider.js
 * vim:ft=javascript.jsx:
 */
import React from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';

import { getPhotoThumb } from '../../../utils/Generic';

import '../../../static/less/global/slider.less';
import '../../../static/css/slick.css';

// These are also referenced by DoubleSlider
export const IMAGE_DIMENSIONS = {
  xs: { width: 180, height: 120 },
  sm: { width: 500, height: 250 },
  md: { width: 800, height: 400 },
  xl: { width: 1200, height: 750 },
  thumbnail: { width: 50, height: 50 },
};

const oneSlideSettings = {
  arrows: false,
  swipe: false,
  dots: false,
  infinite: false, // Si no le ponemos eso se ve 3 veces la imagen cuando es una sóla imagen
  slidesToShow: 1,
  slidesToScroll: 1,
};

const multiSlideSettings = {
  slidesToShow: 1,
  slidesToScroll: 1,
  arrows: true,
  infinite: true, // Makes it cycle
  dots: true,
  speed: 500,
  adaptativeHeight: true,
};

const delayToShowOverlay = 1200;
const delayToHideOverlay = 2000;

class PictureSlider extends React.Component {
  constructor(props) {
    super(props);

    this.state = { showOverlay: false };
    this.sliderSettings = this.props.photos.length > 1 ? multiSlideSettings : oneSlideSettings;
    this.overlayState = this.overlayState.bind(this);

    this.overlayTimeout = null;
  }

  componentWillUnmount() {
    if (this.overlayTimeout) {
      clearTimeout(this.overlayTimeout);
    }
  }

  isSmallerThanMobile(size) {
    return size === 'xs';
  }

  getPhotoDimensions() {
    if (this.isSmallerThanMobile(this.props.imageSize)) {
      return IMAGE_DIMENSIONS[this.props.imageSize];
    }
    if (this.props.isMobile) {
      return IMAGE_DIMENSIONS.sm;
    }
    const dimension = IMAGE_DIMENSIONS[this.props.imageSize];
    return dimension || IMAGE_DIMENSIONS.sm; // fallback is always the small size
  }

  overlayState(mouseIn) {
    return () => {
      // When the user remains "static" on the tile for "delayToShowOverlay", we show the overlay.
      // A second timeout is launched, so that the overlay is hidden after "delayToHideOverlay".
      // Then the overlay is disabled, so that it's only seen once
      if (this.overlayTimeout === 'disabled') {
        return;
      }
      if (mouseIn && !this.overlayTimeout) {
        this.setState({ showOverlay: false });
        this.overlayTimeout = setTimeout(() => {
          this.setState({ showOverlay: true });
          this.overlayTimeout = setTimeout(() => {
            this.setState({ showOverlay: false });
            clearTimeout(this.overlayTimeout);
            this.overlayTimeout = 'disabled';
          }, delayToHideOverlay);
        }, delayToShowOverlay);
      } else if (this.state.showOverlay) {
        this.setState({ showOverlay: false });
      } else if (this.overlayTimeout) {
        clearTimeout(this.overlayTimeout);
        this.overlayTimeout = null;
      }
    };
  }

  renderGallery() {
    const { photos } = this.props;
    const { width, height } = this.getPhotoDimensions();
    if (photos.length === 0) {
      return [
        <img key="base-img" src={`http://dummyimage.com/${width}x150/e6e6e6/e6e6e6`} width={width} height={150} />,
      ];
    }

    /* When there is an overlay, the click happens on it */
    const sortedPhotos = photos.sort((a, b) => a.order - b.order);
    return sortedPhotos.map((photo, index) => (
      getPhotoThumb({
        url: photo.url, width: width.toFixed(0), height: height.toFixed(0), props: { key: index },
      })
    ));
  }

  renderOverlay() {
    const { onPictureClicked, photos } = this.props;
    if (!onPictureClicked || photos.length === 0) {
      return null;
    }
    return (
      <div className="overlay pointer" onClick={onPictureClicked}>
        <div className="content">
          <i className="fa fa-search" />
          <div>{this.context.t('View gallery')}</div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <div
        className={`PictureSlider ${this.state.showOverlay ? ' hovered' : ''}`}
        onMouseEnter={this.overlayState(true)}
        onMouseLeave={this.overlayState(false)}
      >
        <i className="fa fa-expand expand-icon" />
        <div className="SliderGallery">
          <Slider {...this.sliderSettings}>
            {this.renderGallery()}
          </Slider>
          {this.renderOverlay()}
        </div>
      </div>
    );
  }
}

PictureSlider.propTypes = {
  isMobile: PropTypes.bool,
  onPictureClicked: PropTypes.func,
  photos: PropTypes.array.isRequired,
  imageSize: PropTypes.oneOf([
    'xs', // Hotels page, Thumbnail for Mobile
    'sm', // Mobile pictures
    'md', // RoomTile images on Desktop
    'xl', // RoomDetails on Desktop,
  ]),
};

PictureSlider.defaultProps = {
  imageSize: 'md',
};

PictureSlider.contextTypes = {
  t: PropTypes.func.isRequired,
};

export default PictureSlider;
